import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CloseIcon from "../../../assets/icons/close.svg";
import Select, { components } from "react-select";
import APIVariables from "../../../services/api-variables";
import { ReactComponent as DropdownArrowDown } from "../../../assets/icons/dropdownArrowDown.svg";
import { ReactComponent as DropdownArrowUp } from "../../../assets/icons/dropdownArrowUp.svg";
import { handleCatch } from "../../../shared/helpers";
import { App } from "../../../routes/Constants";
import QuitModal from "../../common/Modal/QuitModal";
import ConfirmOutpatientModal from "./modals/ConfirmOutpatientModal";
import { ReactComponent as Undo } from "../../../assets/icons/undo.svg";
import Axios from "../../../services/Axios";

const inputStyles =
  "mt-2.5 mb-[15px] w-full border-0 border-b border-[#c8c8c8] bg-[#f8f8f8] px-0 py-1 text-lg leading-[22px] text-[#646464] outline-none disabled:cursor-not-allowed";

const DropdownIndicator = (props) => {
  return (
    components.DropdownIndicator && (
      <components.DropdownIndicator {...props}>
        {props.selectProps.menuIsOpen ? (
          <DropdownArrowUp />
        ) : (
          <DropdownArrowDown />
        )}
      </components.DropdownIndicator>
    )
  );
};

const OutpatientAdd = () => {
  const navigate = useNavigate();
  const [outpatients, setOutpatients] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [quitModalIsOpen, setIsOpenQuit] = useState(false);
  const [confirmModalIsOpen, setConfirmModal] = useState(false);
  const [validateScreenFields, setValidateScreenFields] = useState({});
  const [form, setForm] = useState({});

  useEffect(() => {
    setValidateScreenFields(validateForm());

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  useEffect(() => {
    getOutpatients();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getOutpatients = async () => {
    await Axios.get(APIVariables.HCP_OUTPATIENTS_ALL)
      .then((res) => {
        const { status, data } = res;
        if (status === 200) {
          setOutpatients(data);
        }
      })
      .catch((error) => {
        handleCatch(error);
      });
  };

  const handleChange = (e) => {
    if (!e.target) {
      setForm((prevState) => ({
        ...prevState,
        outpatient: e,
      }));
    } else {
      const { name, value } = e.target;

      setForm((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    }
  };

  function closeForm() {
    setIsOpenQuit(true);
  }

  function closeModalQuit() {
    setIsOpenQuit(false);
  }

  function quitModal() {
    navigate(App.CONNECTED_OUTPATIENT);
  }

  const submitForm = async () => {
    setIsSubmitting(false);
    let areAllFields = Object.values(validateScreenFields).every((field) => {
      return field.isValid;
    });

    const payload = {
      connected_user_id: form?.outpatient?.connected_user_id,
    };

    if (areAllFields) {
      await Axios.post(APIVariables.USERS_SIGNUP_HCP_OUTPATIENT, payload)
        .then((res) => {
          const { status } = res;
          if (status === 200) {
            setConfirmModal(true);
          }
        })
        .catch(function (error) {
          handleCatch(error);
        });
    } else {
      setIsSubmitting(true);
    }
  };

  const validateForm = () => {
    const { outpatient } = form;
    return {
      outpatient: {
        isValid: outpatient && Object.keys(outpatient).length > 0,
      },
    };
  };

  const outpatientMapOptions = () => {
    return (outpatients || []).map((outpatient) => ({
      label: (
        <div className="flex-justify custom-hover-select">
          <div>{outpatient?.name}</div>
          <div>{outpatient?.postcode}</div>
        </div>
      ),
      value: outpatient.name + " " + outpatient.postcode,
      ...outpatient,
    }));
  };

  return (
    <div
      className="min-h-screen overflow-y-auto  pt-[140px] pb-[100px]"
      data-testid="connected-outpatients-add-page"
    >
      <div className="relative mx-auto max-w-[1100px] rounded-[10px] bg-white px-6 py-5 shadow-[0px_0px_40px_rgba(0,0,0,0.2)] sm:px-10">
        <button
          type="button"
          className="absolute right-[15px] top-[15px] cursor-pointer rounded-full bg-[#9ABDB8] p-[3px]"
          onClick={closeForm}
          aria-label="Close form"
          data-testid="connected-outpatients-add-close-button"
        >
          <img src={CloseIcon} alt="" className="brightness-0 invert" />
        </button>
        <div className="font-size-18 blue-label">Step 1/1</div>
        <h1 className="select-category-txt mineShaft-color mt-2.5 pb-1.5">
          Outpatient Pharmacy Connection Form
        </h1>
        <hr className="my-0 h-px border-0 bg-[#768692]" />

        <h2 className="title-details mineShaft-color mt-[30px] mb-[25px]">
          Outpatient Details
        </h2>

        <div className="mb-5">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="relative mb-1 custom-select-placeholder">
              <label className="blue-label" htmlFor="outpatient-select">
                Outpatient Pharmacy Name*:
              </label>
              <Select
                inputId="outpatient-select"
                classNamePrefix="custom-select"
                components={{
                  DropdownIndicator: DropdownIndicator,
                  IndicatorSeparator: () => null,
                }}
                value={form.outpatient}
                name="outpatient"
                onChange={handleChange}
                options={outpatientMapOptions()}
                placeholder="Search Pharmacy name or Postcode"
                className={
                  isSubmitting && !validateScreenFields.outpatient?.isValid
                    ? "error-select"
                    : "select-border"
                }
                data-testid="connected-outpatients-add-select"
              />
              {isSubmitting && !validateScreenFields.outpatient?.isValid && (
                <span className="required-text">Required</span>
              )}
            </div>
            <div>
              <label className="blue-label" htmlFor="outpatient-postcode">
                Postcode:
              </label>
              <input
                id="outpatient-postcode"
                type="text"
                name="postcode"
                value={form.outpatient?.postcode || ""}
                disabled
                className={inputStyles}
                data-testid="connected-outpatients-add-postcode-input"
              />
            </div>
          </div>
          <div className="mt-4">
            <label className="blue-label" htmlFor="outpatient-address">
              Outpatient Address:
            </label>
            <input
              id="outpatient-address"
              type="text"
              name="address"
              value={form.outpatient?.address || ""}
              disabled
              className={inputStyles}
              data-testid="connected-outpatients-add-address-input"
            />
          </div>
          <p className="info_nurse font-size-16 mt-9 leading-[15px] blue-label">
            <b className="font-size-16">IMPORTANT</b>: Connected Outpatient Pharmacy
            will only receive an email of any PAF that has been sent
          </p>
        </div>

        <div className="flex flex-col gap-4 pt-[60px] sm:flex-row sm:items-center sm:justify-between">
          <p
            className="text-base blue-label"
            data-testid="connected-outpatients-add-mandatory-field-note"
          >
            *symbol indicating a mandatory field
          </p>
          <div className="flex items-center justify-end gap-3">
            {form?.outpatient && Object.keys(form?.outpatient).length ? (
              <button
                type="button"
                className="flex items-center gap-1 border-0 bg-transparent font-size-18 text-[#00567a] hover:text-[#8c8c8c]"
                onClick={() => setForm({ outpatient: "" })}
                data-testid="connected-outpatients-add-undo-button"
              >
                <Undo /> Undo Changes
              </button>
            ) : null}

            <button
              type="button"
              className="primary-button"
              onClick={submitForm}
              data-testid="connected-outpatients-add-save-button"
            >
              Save
            </button>
          </div>
        </div>
      </div>

      {quitModalIsOpen && (
        <QuitModal
          quitModalIsOpen={quitModalIsOpen}
          closeModalQuit={closeModalQuit}
          quitModal={quitModal}
          description="Are you sure you want to cancel filling out this form? All progress will be lost and you will need to start again."
        />
      )}
      {confirmModalIsOpen && (
        <ConfirmOutpatientModal
          closeModal={setConfirmModal}
          modalIsOpen={confirmModalIsOpen}
        />
      )}
    </div>
  );
};
export default OutpatientAdd;
